import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useSavedSearches } from '@/hooks/queries'

/**
 * Result returned by `useFilterDefaultsFromPrefs`.
 *
 * `defaults` is `undefined` when:
 *   - the user is unauthenticated, OR
 *   - no saved search matches the given `filterKey`.
 *
 * Captain Input #4 (locked, 2026-05-02 spec): existing users with no
 * preferences continue to get the global view. We do NOT synthesise defaults
 * on their behalf and do NOT prompt for migration.
 *
 * `hasSavedDefault` lets adopting components distinguish "no saved filter"
 * from "saved filter is empty object" without leaking `undefined` into the
 * component's filter state shape.
 */
export interface FilterDefaultsResult<T> {
  defaults: T | undefined
  isLoading: boolean
  hasSavedDefault: boolean
}

/**
 * Reads the user's saved searches and returns the `filters` payload of the
 * one whose `name` matches `filterKey`, mapped to the caller's filter shape.
 *
 * Used by sibling Wave-1 filter-rail PRs (A1a/A1b/A1c/A1e/A1f/A1g) to seed
 * page-level filter state from prefs. P1's ESLint rule
 * `filter-rail-must-adopt-prefs` enforces that allowlisted list pages CALL
 * this hook from their filter init.
 *
 * Implementation notes:
 *   - Built on top of the existing `useSavedSearches` hook (no new endpoint).
 *   - Returns the LATEST saved search if multiple share the same `name`
 *     (sorted by `createdAt` desc) — server uniqueness is loose, but the
 *     client is deterministic.
 *   - Returns `undefined` defaults instead of `null` so adopting components
 *     can spread the result into their `useState` initialiser without a
 *     conditional: `useState<F>(defaults ?? { /* page-level globals *\/ })`.
 *
 * @example
 *   const { defaults, isLoading, hasSavedDefault } =
 *     useFilterDefaultsFromPrefs<StoryFilters>('stories')
 *   const [filters, setFilters] = useState<StoryFilters>(
 *     defaults ?? { limit: 20 },
 *   )
 */
export function useFilterDefaultsFromPrefs<T>(
  filterKey: string,
): FilterDefaultsResult<T> {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const savedSearchesQuery = useSavedSearches(userId)

  return useMemo<FilterDefaultsResult<T>>(() => {
    if (!userId) {
      return { defaults: undefined, isLoading: false, hasSavedDefault: false }
    }
    if (savedSearchesQuery.isLoading) {
      return { defaults: undefined, isLoading: true, hasSavedDefault: false }
    }
    const matches = (savedSearchesQuery.data ?? []).filter(
      (s) => s.name === filterKey,
    )
    if (matches.length === 0) {
      return { defaults: undefined, isLoading: false, hasSavedDefault: false }
    }
    // Sort newest-first by createdAt — last-write-wins.
    const latest = matches.reduce((acc, candidate) =>
      candidate.createdAt > acc.createdAt ? candidate : acc,
    )
    return {
      defaults: latest.filters as T,
      isLoading: false,
      hasSavedDefault: true,
    }
  }, [userId, savedSearchesQuery.isLoading, savedSearchesQuery.data, filterKey])
}
